import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../utils/axiosAdvanced';

// API returns HTTP 200 for errors; check res.data.error
const normalizeError = (res) => {
    if (!res) return { error: 'No response' };
    if (res?.data?.error) return { error: res.data.error, meta: res.data };
    return null;
};

export const buildGensetQueryKey = (payload = {}) => {
    const {
        user_id = '',
        imeis = [],
        timeFrom = '',
        timeTo = '',
        overuseThresholdMin = 240,
        genset_lph = 2,
        diesel_price = undefined,
    } = payload || {};
    const imeiList = Array.isArray(imeis) ? [...imeis].sort().join(',') : '';
    const pricePart = (diesel_price === undefined || diesel_price === null) ? '' : String(diesel_price);
    return [user_id, timeFrom, timeTo, imeiList, overuseThresholdMin, genset_lph, pricePart].join('|');
};

export const fetchMultiGensetSummary = createAsyncThunk(
    'genset/fetchMultiSummary',
    async (payload, thunkAPI) => {
        try {
            const res = await apiClient.post('/multi_genset_summary', payload);
            const err = normalizeError(res);
            if (err) return thunkAPI.rejectWithValue(err);
            return { data: res.data, queryKey: buildGensetQueryKey(payload) };
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error?.message || 'Request failed' });
        }
    }
);

export const fetchGensetSetup = createAsyncThunk(
    'genset/fetchSetup',
    async (payload, thunkAPI) => {
        try {
            const res = await apiClient.post('/genset_setup', payload);
            const err = normalizeError(res);
            if (err) return thunkAPI.rejectWithValue(err);
            return { data: res.data, request: payload };
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error?.message || 'Request failed' });
        }
    }
);

export const fetchGensetStatusHydration = createAsyncThunk(
    'genset/fetchStatusHydration',
    async (payload, thunkAPI) => {
        try {
            const res = await apiClient.post('/genset_status_hydration', payload);
            const err = normalizeError(res);
            if (err) return thunkAPI.rejectWithValue(err);
            return { data: res.data, request: payload };
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error?.message || 'Request failed' });
        }
    }
);

export const fetchGensetStatusRefresh = createAsyncThunk(
    'genset/fetchStatusRefresh',
    async (payload, thunkAPI) => {
        try {
            const state = thunkAPI.getState()?.genset || {};
            const cacheKey = payload?.cacheKey || state?.status?.cacheKey;
            if (!cacheKey) {
                return thunkAPI.rejectWithValue({ error: 'Missing cacheKey' });
            }
            const clientStateVersion = payload?.clientStateVersion ?? state?.status?.stateVersion ?? null;
            const refreshMode = payload?.refreshMode || 'delta';
            const reqPayload = { cacheKey, refreshMode };
            if (clientStateVersion !== null && clientStateVersion !== undefined) {
                reqPayload.clientStateVersion = clientStateVersion;
            }
            const res = await apiClient.post('/genset_status_refresh', reqPayload);
            const err = normalizeError(res);
            if (err) return thunkAPI.rejectWithValue(err);
            return { data: res.data, request: reqPayload };
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error?.message || 'Request failed' });
        }
    }
);

export const fetchGensetRuntimeCheck = createAsyncThunk(
    'genset/fetchRuntimeCheck',
    async (payload, thunkAPI) => {
        try {
            const res = await apiClient.post('/genset_runtime_check', payload);
            const err = normalizeError(res);
            if (err) return thunkAPI.rejectWithValue(err);
            return { data: res.data, request: payload };
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error?.message || 'Request failed' });
        }
    }
);
